/**
 * 결재함 조건 모델 (2026-09-17 · 결재함 툴바 개편).
 *
 * ★★★ **이 파일이 「무엇이 서버로 가는가」의 단 하나의 자리다.**
 *
 *    까닭 — 이 서버의 fastify ajv 는 `removeAdditional` 이라 **스키마에 없는 칸을 400 으로 막지 않고
 *    조용히 지운 채 200** 을 준다(`approval.routes.ts:77` `additionalProperties: false`).
 *    그래서 화면이 모르는 조건을 실어 보내면 **실패처럼 보이지 않고** 「눌러도 아무 일이 없는 칸」이 된다 —
 *    사용자는 조건이 걸린 줄 알고 **틀린 목록을 믿는다.** 눈으로는 영영 안 보이는 자리라
 *    `buildListParams()` 라는 **잴 수 있는 함수**로 경계를 옮기고 시험으로 잠근다.
 *
 * ★ 서버가 실제로 받는 칸은 여섯뿐이다(`approval.routes.ts` `listQuerySchema`):
 *     view · cursor · limit · q · status · deptBoxId
 *   그 밖의 조건은 **화면에 「서버 준비 중」으로 그리되 «보내지 않는다»**. 빼지 않는 것은
 *   「우리 기능이 아니니 뺀다」를 화면이 정하지 않기 때문이다 — 빼는 것은 사용자가 정한다.
 */

/** 서버 `listQuerySchema` 가 받는 칸 — 이 목록 밖은 요청에 실리지 않는다. */
export const SERVER_FILTER_KEYS = ['view', 'cursor', 'limit', 'q', 'status', 'deptBoxId'] as const;
export type ServerFilterKey = (typeof SERVER_FILTER_KEYS)[number];

/**
 * 화면에는 있으나 **서버가 아직 안 받는** 조건.
 * ⚠ 여기 있는 이름이 `buildListParams()` 결과에 하나라도 들어가면 그것이 곧 결함이다(시험이 잠근다).
 */
export const PENDING_FILTER_KEYS = ['period', 'docType', 'drafter', 'deptId', 'urgentOnly'] as const;
export type PendingFilterKey = (typeof PENDING_FILTER_KEYS)[number];

/** 「서버 준비 중」 칸에 붙는 까닭 — 화면과 후속 서버 요청 목록이 같은 말을 쓴다. */
export const PENDING_FILTER_REASON = '서버 준비 중';

/**
 * ★★★ **조건을 지우는 단추의 낱말 — 한 글자로 못 박는다** (2026-09-17 사용자 결정).
 *
 *    사용자는 처음 「조건초기화」라고 했으나, 이 프로젝트 낱말 기준이 **「초기화」를 피하고**
 *    「되돌리기」 · 「해제」를 쓰라 하고 정본 P3 「목록」의 문구가 **「필터 지우기」**라
 *    기준 쪽으로 정하셨다. → **어디서든 이 상수를 쓴다.**
 * ⚠ 이 낱말은 **컴파일이 안 잡아 준다** — 다음 사람이 「초기화」로 되돌려도 빌드는 통과한다.
 *   그래서 상수 하나로 모으고 시험(`approval-filters.test.ts`)이 글자와 «자리 수»를 함께 잠근다.
 */
export const CLEAR_FILTERS_LABEL = '필터 지우기';

export interface ApprovalFilters {
  /** 서버로 간다 — 제목 · 문서번호 · 기안자 이름(최대 100자). */
  q: string;
  /** 서버로 간다 — `view=involved` 의 상태. 트리 마디와 **같은 값 하나**를 쓴다. */
  status: string | null;
  /** 서버로 간다 — `view=dept` 의 한 함. */
  deptBoxId: string | null;
  // ── 아래는 화면에만 있다. «보내지 않는다». ──────────────────────────
  period: string | null;
  docType: string | null;
  drafter: string;
  deptId: string | null;
  urgentOnly: boolean;
}

export const EMPTY_FILTERS: ApprovalFilters = {
  q: '', status: null, deptBoxId: null,
  period: null, docType: null, drafter: '', deptId: null, urgentOnly: false,
};

function has(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'boolean') return v;
  return true;
}

/**
 * 요청 파라미터를 만든다 — **서버가 받는 칸만.**
 *
 * ★ 「준비 중」 조건이 아무리 채워져 있어도 결과에 **한 칸도 실리지 않는다.**
 * ★ 빈 값은 애초에 넣지 않는다(옛 서버가 빈 문자열을 조건으로 오해하지 않게).
 */
export function buildListParams(
  f: ApprovalFilters,
  extra?: { limit?: number; cursor?: string },
): Partial<Record<ServerFilterKey, string | number>> {
  const out: Partial<Record<ServerFilterKey, string | number>> = {};
  const term = f.q.trim().slice(0, 100);
  if (term !== '') out.q = term;
  if (has(f.status)) out.status = f.status as string;
  if (has(f.deptBoxId)) out.deptBoxId = f.deptBoxId as string;
  if (extra?.limit !== undefined) out.limit = extra.limit;
  if (extra?.cursor !== undefined && extra.cursor !== '') out.cursor = extra.cursor;
  return out;
}

/**
 * 「조건 몇 개가 걸렸나」 — 아이콘 옆 숫자와 칩 줄이 **같은 셈**을 쓴다.
 * ★ **검색어는 세지 않는다** — 검색칸은 제 모습으로 이미 보이고, 조건필터의 숫자는
 *   「접혀 있는 곳에 무엇이 걸렸나」를 말하는 것이라 뜻이 다르다.
 */
export function activeFilterCount(f: ApprovalFilters): number {
  let n = 0;
  if (has(f.status)) n += 1;
  if (has(f.deptBoxId)) n += 1;
  for (const k of PENDING_FILTER_KEYS) if (has(f[k])) n += 1;
  return n;
}

/** 조건이 하나라도 걸렸나(검색어 제외). */
export function hasActiveFilters(f: ApprovalFilters): boolean {
  return activeFilterCount(f) > 0;
}

/** 조건 또는 검색어가 걸렸나 — 0건일 때 「지우기」를 세울지 가른다(정본 P3 `no-result`). */
export function isNarrowed(f: ApprovalFilters): boolean {
  return hasActiveFilters(f) || f.q.trim() !== '';
}

/**
 * 조건을 지운다 — **검색어(`q`)는 남긴다.**
 * ★ 친 글자를 잃지 않게 하려는 것이다(되돌릴 수 없는 동작이라 좁게 잡았다).
 *   검색어까지 지우려면 부르는 쪽이 `{ ...clearFilters(f), q: '' }` 로 명시한다.
 */
export function clearFilters(f: ApprovalFilters): ApprovalFilters {
  return { ...EMPTY_FILTERS, q: f.q };
}
